import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Pages racines de l'application (pas de parent, on va au dashboard)
 */
const ROOT_PAGES = ['/dashboard', '/folders', '/procedures', '/tokens', '/search', '/help', '/settings/devices'];

/**
 * Segments qui ne sont pas des pages valides (juste des préfixes d'URL).
 * Si on tombe sur un de ces segments en dernière position après avoir remonté,
 * on continue de remonter.
 */
const VIRTUAL_SEGMENTS = ['reports', 'interventions', 'pictures', 'edit', 'create', 'preview', 'send'];

/**
 * Trouve le parent logique d'un chemin en remontant d'un niveau dans l'URL.
 *
 * Exemples:
 * - /procedures/abc/reports/123 → /procedures/abc (saute "reports" car virtuel)
 * - /folders/123/456/pictures/789 → /folders/123/456 (saute "pictures" car virtuel)
 * - /folders/123/interventions/456 → /folders/123 (saute "interventions" car virtuel)
 * - /procedures/abc/edit → /procedures/abc
 * - /folders → /dashboard (page racine)
 *
 * @param {string} pathname - Le chemin actuel
 * @returns {string} - Le chemin du parent
 */
function getParentPath(pathname) {
    // Nettoyer le chemin
    const cleanPath = pathname.replace(/\/$/, '') || '/';

    // Si c'est une page racine, aller au dashboard
    if (ROOT_PAGES.includes(cleanPath) || cleanPath === '/') {
        return '/dashboard';
    }

    // Remonter d'un niveau (supprimer le dernier segment)
    let segments = cleanPath.split('/').filter(Boolean);

    if (segments.length <= 1) {
        return '/dashboard';
    }

    // Retirer le dernier segment
    segments = segments.slice(0, -1);

    // Si le nouveau dernier segment est un segment virtuel (pas une vraie page),
    // continuer de remonter
    while (segments.length > 1 && VIRTUAL_SEGMENTS.includes(segments[segments.length - 1])) {
        segments = segments.slice(0, -1);
    }

    const result = '/' + segments.join('/');

    // Si on est arrivé à une page racine ou si le chemin est vide
    if (segments.length <= 1 || ROOT_PAGES.includes(result)) {
        return result || '/dashboard';
    }

    return result;
}

/**
 * Hook pour une navigation "retour" sûre qui remonte dans l'arborescence des URLs.
 *
 * @returns {{ goBack: () => void, parentPath: string }}
 *
 * @example
 * const { goBack } = useSafeBack();
 * // Sur /procedures/abc/reports/123 → va vers /procedures/abc
 * // Sur /folders/123/456/pictures/789 → va vers /folders/123/456
 */
export function useSafeBack() {
    const navigate = useNavigate();
    const location = useLocation();

    const parentPath = getParentPath(location.pathname);

    const goBack = useCallback(() => {
        // Si l'utilisateur a un historique de navigation interne, revenir en arrière

        if (window.history.length > 1 && location.key !== 'default') {
            navigate(-1);
        } else {
            navigate(parentPath, { replace: true });
        }
    }, [navigate, parentPath, location.key]);

    return { goBack, parentPath };
}

export { getParentPath };
